<template>
    <!-- 尺码指南弹框: PC 居中弹窗, 移动端(≤768px)全屏; 数据由父级传入 -->
    <div class="size-guide-dialog" v-if="visible">
        <div class="size-guide-mask" @click="close"></div>
        <div class="size-guide-container">
            <div class="size-guide-header">
                <h3 class="size-guide-title">尺码指南</h3>
                <i class="iconfont-ot-website close size-guide-close" @click="close">&#xe6df;</i>
            </div>
            <div class="size-guide-body">
                <!-- 试穿报告: size_guide.fit_notes 数组(暂用 mock, 后续由接口返回), 展示在尺码表最前面 -->
                <div class="size-guide-table size-guide-fit" v-for="(fit, f) in fitNotes" :key="'fit' + f">
                    <h4 class="size-guide-table-name" v-if="fit.name">{{ fit.name }}</h4>
                    <!-- 标题行: 在滚动容器之外, 结构性固定, 不随数据滑动; 列头或行数据缺失时整个表格区域不渲染(示意图/提示保留) -->
                    <div class="fit-top-row" v-if="hasFitGrid(fit)">
                        <div class="fit-fixed-col">
                            <table class="fit-grid">
                                <tr class="size-guide-row">
                                    <th class="fit-cell fit-head" :colspan="(fit.row_labels || []).length">试穿信息</th>
                                </tr>
                            </table>
                        </div>
                        <div class="fit-head-row">
                            <table class="fit-grid">
                                <tr class="size-guide-row">
                                    <th class="fit-cell fit-head" :colspan="(fit.size_columns || []).length">试穿尺码</th>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <!-- 数据行: 左右两块, 标签列(脚长/脚型+数据)在滚动容器外, 滚动条从标签列右侧开始(对齐官网/尺码表) -->
                    <div class="fit-body" v-if="hasFitGrid(fit)">
                        <div class="fit-fixed-col">
                            <table class="fit-grid">
                                <tr class="size-guide-row">
                                    <th class="fit-cell fit-head" v-for="(label, i) in fit.row_labels" :key="'l' + i">{{
                                        label }}</th>
                                </tr>
                                <tr :class="['size-guide-row', r % 2 === 0 ? 'striped' : '']"
                                    v-for="(row, r) in fit.rows" :key="r">
                                    <td class="fit-cell">{{ row.row_value1 }}</td>
                                    <td class="fit-cell">{{ row.row_value2 }}</td>
                                </tr>
                            </table>
                        </div>
                        <div class="fit-layout">
                            <div class="fit-right">
                                <table class="fit-grid">
                                    <tr class="size-guide-row">
                                        <th class="fit-cell fit-head" v-for="(col, i) in fit.size_columns"
                                            :key="'c' + i">{{ col }}</th>
                                    </tr>
                                    <tr :class="['size-guide-row', r % 2 === 0 ? 'striped' : '']"
                                        v-for="(row, r) in fit.rows" :key="r">
                                        <td class="fit-cell" v-for="(col, i) in fit.size_columns" :key="i">
                                            {{ row.fit_data ? row.fit_data[col] : '' }}</td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                    <!-- 试穿示意图: PC 用 pc_images, 移动端用 mob_images; 括号保证 || 兜底作用于两个分支 -->
                    <div class="fit-images" v-if="((isMobile() ? fit.mob_images : fit.pc_images) || []).length">
                        <img v-for="(img, n) in isMobile() ? fit.mob_images : fit.pc_images" :key="n" :src="img"
                            alt="" />
                    </div>
                    <!-- 试穿提示: 样式同已有 notes -->
                    <p class="size-guide-note" v-if="fit.fit_notes">{{ fit.fit_notes }}</p>
                </div>
                <!-- 尺码表: rows 为各测量行 -->
                <template v-if="data && data.tables && data.tables.length">
                    <div class="size-guide-table" v-for="(table, t) in data.tables" :key="t">
                        <h4 class="size-guide-table-name">{{ table.displayName }}</h4>
                        <!-- 左右两块: 标签列固定在滚动容器外, 横向滚动条从标签列右侧开始(对齐官网) -->
                        <div class="size-guide-table-layout">
                            <div class="size-guide-fixed-col">
                                <table class="size-guide-grid">
                                    <tr class="size-guide-row" v-for="(row, r) in table.rows" :key="r">
                                        <th class="size-guide-label">{{ formatLabel(row.label) }}</th>
                                    </tr>
                                </table>
                            </div>
                            <div class="size-guide-table-wrap">
                                <table class="size-guide-grid">
                                    <!-- 测量行 -->
                                    <tr class="size-guide-row" v-for="(row, r) in table.rows" :key="r">
                                        <td class="size-guide-value" v-for="(v, c) in row.values" :key="c">{{ v }}</td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                        <p class="size-guide-note" v-if="table.note">{{ table.note }}</p>
                    </div>
                </template>

                <!-- 测量示意图: PC 用 images.pcImg, 移动端用 images.mobImg; 存在非空 label 时才显示 tab 栏 -->
                <div class="size-guide-tabs" v-if="data && data.tabs && data.tabs.length">
                    <div class="size-guide-tab-bar" v-if="hasTabLabel">
                        <span :class="['size-guide-tab', activeTab === tab.key ? 'active' : '']"
                            v-for="tab in data.tabs" :key="tab.key" @click="activeTab = tab.key">{{ tab.label }}</span>
                    </div>
                    <div class="size-guide-tab-panel" v-for="tab in data.tabs" :key="tab.key"
                        v-show="!hasTabLabel || activeTab === tab.key">
                        <!-- tab 缺 images 时跳过渲染, 避免访问 .mobImg/.pcImg 抛错导致整个弹窗崩溃 -->
                        <template v-if="tab.images">
                            <img v-for="(img, n) in isMobile() ? tab.images.mobImg : tab.images.pcImg" :key="n"
                                :src="img" :alt="tab.label" />
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import utils from "@/utils";

export default {
    name: "SizeGuideDialog",
    props: {
        // 显隐: 父级用 :visible.sync 控制
        visible: {
            type: Boolean,
            default: false,
        },
        // 尺码指南数据(接口 size_guide): { id, name, tables: [{displayName, rows, note}], tabs: [{key, label, images: {pcImg: [], mobImg: []}}] }
        // 注意: 商品仅有试穿报告时 info.size_guide 为 null, prop default 不接管 null, 模板与逻辑均需先判空
        data: {
            type: Object,
            default: () => ({}),
        },
        // 试穿报告(info.fit_notes, 后续由接口返回): 展示在尺码表最前面
        fitNotes: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            activeTab: "",
        };
    },
    methods: {
        close() {
            this.$emit("update:visible", false);
        },
        // label 含括号(中英文)时在括号前换行, 配合 label 的 white-space: pre-line
        formatLabel(label) {
            return String(label == null ? "" : label).replace(/([(（])/, "\n$1");
        },
        // 示意图区分 PC/移动端图片
        isMobile: utils.isMobile,
        // 表格区域(表头+数据)渲染条件: 列头与行数据齐全; 缺失时仅显示示意图/提示
        hasFitGrid(fit) {
            return !!((fit && fit.size_columns || []).length && (fit && fit.rows || []).length);
        },
    },
    computed: {
        // tab 栏仅在存在非空 label 时显示
        hasTabLabel() {
            return ((this.data && this.data.tabs) || []).some((tab) => tab.label);
        },
    },
    watch: {
        visible(val) {
            // 每次打开重置到第一个 tab, 并锁定页面滚动;
            // 锁定时补 paddingRight = 滚动条宽度, 防止滚动条消失导致页面宽度跳动
            if (val) {
                const tabs = (this.data && this.data.tabs) || [];
                this.activeTab = (tabs[0] && tabs[0].key) || "";
                const scrollbarWidth =
                    window.innerWidth - document.documentElement.clientWidth;
                document.body.style.paddingRight = scrollbarWidth + "px";
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "";
                document.body.style.paddingRight = "";
            }
        },
    },
    // 弹框未关闭时组件被销毁(如路由切换), 恢复页面滚动
    beforeDestroy() {
        if (this.visible) {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        }
    },
};
</script>

<style lang="scss" scoped>
.size-guide-dialog {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;

    .size-guide-mask {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
    }

    .size-guide-container {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 840px;
        max-width: calc(100vw - 40px);
        max-height: 86vh;
        background: #fff;
        display: flex;
        flex-direction: column;

        .size-guide-header {
            flex: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 36px 40px 16px;

            .size-guide-title {
                font-size: 24px;
                font-weight: 700;
                color: #000;
            }

            .size-guide-close {
                font-size: 32px;
                line-height: 1;
                cursor: pointer;
            }
        }

        .size-guide-body {
            overflow-y: auto;
            padding: 0 40px 36px;
        }

        .size-guide-table {
            padding: 20px 0;

            .size-guide-table-name {
                font-size: 18px;
                font-weight: 700;
                color: #000;
                text-align: left;
                margin-bottom: 10px;
            }

            // 左右布局: 标签列固定在滚动容器外, 滚动条从标签列右侧开始(对齐官网)
            .size-guide-table-layout {
                display: flex;
                align-items: flex-start;

                .size-guide-fixed-col {
                    flex: none;
                    width: 74px; // 与标签列宽一致
                    background: #fff;
                }
            }

            // 滚动区: 超出容器宽度时横向滑动; 底部留 8px 让滚动条与表格隔开(对齐官网)
            .size-guide-table-wrap {
                flex: 1;
                min-width: 0; // 允许 flex 子项收缩到内容宽以下, 否则不会出现横向滚动
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;
                padding-bottom: 8px;

                // 仅滚动区表格按内容自适应展开
                .size-guide-grid {
                    min-width: max-content;
                }
            }

            .size-guide-grid {
                width: 100%;
                // separate + spacing 0: 间隙全部改由单元格白色 border 形成
                border-collapse: separate;
                border-spacing: 0;

                .size-guide-label {
                    background: #4B4F54;
                    color: #fff;
                    font-weight: 400;
                    font-size: 11px;
                    padding: 6px 8px;
                    text-align: center;
                    width: 74px; // 标签列固定 74px, 保证上下多表对齐
                    white-space: pre-line; // 配合 formatLabel 在括号前换行
                    height: 44px; // 与值行同高, 保证左右两表行对齐
                    line-height: 14px; // 显式行高: 两行文案 28px + padding 12px ≤ 44, 文案超两行会左右错位
                    box-sizing: border-box;
                    // 网格间隙: 单元格自身 1px 白线
                    border-right: 1px solid #fff;
                    border-bottom: 1px solid #fff;
                }

                .size-guide-value {
                    background: #fff;
                    color: #000;
                    font-size: 12px;
                    text-align: center;
                    min-width: 56px; // 值列最小 56px
                    max-width: 150px; // 值列最大 150px, 超出则换行
                    word-wrap: break-word; // 长内容(无空格)也允许换行
                    // 网格间隙: 同上, 单元格白线
                    border-right: 1px solid #fff;
                    border-bottom: 1px solid #fff;
                    height: 44px; // 行高对齐官网
                    padding: 0 4px;

                    &:nth-child(even) {
                        background: #f7f7f7;
                    }
                }
            }

            .size-guide-note {
                margin-top: 20px;
                padding: 10px;
                background: #f9f9f9;
                font-size: 12px;
                color: #333;
                text-align: left;
            }
        }

        .size-guide-tabs {
            margin-top: 20px;

            .size-guide-tab-bar {
                display: flex;
                justify-content: center;
                border-bottom: 3px solid #e7e8ea;

                .size-guide-tab {
                    padding: 5px 30px 18px;
                    margin-bottom: -3px; // active 黑色下划线盖在灰色底线上
                    font-size: 14px;
                    color: #000;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;

                    &.active {
                        font-weight: 700;
                        border-bottom-color: #000;
                    }
                }
            }

            .size-guide-tab-panel {
                margin-top: 20px;
                text-align: center;

                img {
                    display: block;
                    width: 100%; // 图片宽度撑满
                    height: auto;
                }

                // 多张示意图之间的间距
                img+img {
                    margin-top: 12px;
                }
            }
        }

        // 试穿报告(fit_notes): 左侧试穿信息固定, 右侧试穿尺码区域横向滑动;
        // 独立 fit-cell 类避免复用 .size-guide-label(sticky)/.size-guide-value(列条纹)带来的样式污染
        $fit-fixed-w: 150px; // 左侧固定列宽: 两行共用, 保证右区起点对齐

        .size-guide-fit {

            // 间隙不用 border-spacing(与 sticky 固定列配合有透视/偏移问题), 表内网格线由单元格自身白边框形成
            .fit-grid {
                border-collapse: separate;
                border-spacing: 0;
            }

            // 单元格不换行: 左右两表行序一致, 保证滑动时行高对齐
            .fit-cell {
                background: #fff;
                color: #000;
                font-weight: 400;
                font-size: 12px;
                // 显式行高: th/td 默认行高计算有差异, 统一后各表头行等高
                line-height: 17px;
                text-align: center;
                min-width: 56px;
                height: 44px;
                box-sizing: border-box;
                white-space: nowrap;
                // 行列间隙: 右/下 1px 白线(td/th 上 margin 不生效, 故用 border; 表格边缘的白线贴白色背景不可见)
                border-right: 1px solid #fff;
                // border-bottom: 1px solid #fff;
            }

            // 深色表头(试穿信息/试穿尺码 + row_labels/size_columns)
            .fit-head {
                background: #4B4F54;
                color: #fff;
            }

            // 标题行: 在滚动容器之外, 结构性固定, 不随数据滑动
            .fit-top-row {
                display: flex;
                // 与数据行之间的 1px 缝(间距统一用向下 margin, 表格区不渲染时不会留下顶部空隙)
                margin-bottom: 1px;

                .fit-fixed-col {
                    width: $fit-fixed-w;
                    flex: none;
                    background: #fff;
                    // 与右侧"试穿尺码"条之间的 1px 缝
                    // margin-right: 1px;

                    .fit-grid {
                        width: 100%;
                    }
                }

                .fit-head-row {
                    flex: 1;
                    background: #fff;

                    .fit-grid {
                        width: 100%;
                    }
                }
            }

            // 左右布局: 标签列(脚长/脚型+数据)固定在滚动容器外, 滚动条从标签列右侧开始(对齐官网)
            .fit-body {
                // 与标题行之间的 1px 缝
                margin-top: 1px;
                // 与下方示意图的 20px 间距由表格区承担, 表格不渲染时图片上方不残留空隙
                margin-bottom: 20px;
                display: flex;
                align-items: flex-start;

                .fit-fixed-col {
                    flex: none;
                    width: $fit-fixed-w;
                    background: #fff;

                    .fit-grid {
                        width: 100%;
                    }
                }
            }

            // 滚动区: 只含右侧试穿尺码部分, 超出时横向滑动
            .fit-layout {
                flex: 1;
                min-width: 0; // 允许 flex 子项收缩到内容宽以下, 否则不会出现横向滚动
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;

                .fit-right {

                    // 列少时表格撑满可视宽度(单元格最小 56px 自适应摊开), 列多时随内容撑开
                    >.fit-grid {
                        width: 100%;
                        min-width: max-content;
                    }
                }
            }

            // 数据行隔行灰条纹(左右两表按数据行序号 r%2 同步, 不受右侧表头行偏移影响)
            .size-guide-row.striped .fit-cell:not(.fit-head) {
                background: #f7f7f7;
            }

            // 试穿示意图: 多张间距同 tab 面板(间距统一用向下 margin, 无表格时图片上方不残留空隙)
            .fit-images {
                margin-bottom: 20px;
                text-align: center;

                img {
                    display: block;
                    width: 100%;
                    height: auto;
                }

                img+img {
                    margin-bottom: 12px;
                }
            }
        }
    }
}

.size-guide-body,
.size-guide-table-wrap,
.fit-layout {
    scrollbar-width: thin;
    scrollbar-color: #333 #f9f9f9;
}

// 表格间分隔线: 画在后一个表格顶部, 首个无线, 最后一个自然无线(试穿报告与尺码表之间同样生效)
.size-guide-table+.size-guide-table {
    border-top: 1px solid #f2f2f2;
}

@media only screen and (max-width: 768px) {

    // 移动端全屏
    .size-guide-dialog .size-guide-container {
        left: 0;
        top: 0;
        transform: none;
        width: 100%;
        max-width: 100%;
        height: 100%;
        max-height: 100%;

        .size-guide-header {
            padding: 16px 15px 10px;

            .size-guide-close {
                font-size: 24px;
            }

            .size-guide-title {
                font-size: 20px;

            }
        }

        .size-guide-body {
            padding: 0 15px 15px;

            .size-guide-table-name {
                font-size: 17px;
            }

            // 移动端表格数值字体缩小
            .size-guide-grid .size-guide-value {
                font-size: 12px;
            }

            .size-guide-tabs {
                margin-top: 20px;

                .size-guide-tab-bar {
                    .size-guide-tab {
                        font-size: 12px;
                    }
                }
            }
        }
    }
}
</style>
